import { useSearchParams } from 'next/navigation'
import { TFunction, useTranslation } from 'next-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'

import { useQuinceNprogressRouter } from '@src/modules/navigation'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

// Helpers

// Types
import { FEATURES, MODULES } from '../../constants/template-info'
import Product from '../../domains/Product'
import QuinceError from '../../domains/QuinceError'
import ordersAndReturnService from '../../services/ordersAndReturnService'
import { RefundSummary, ReturnResponse } from '../../services/ordersAndReturnService/ordersAndReturn.types'
import { FREE_TOTE_PRODUCT, FREE_TOTE_SPID } from '../cart/constants/freeGiftConstants'

import LineItem from './types/LineItem'
import Order from './types/Order'
import RETURN_TYPES from './types/ReturnTypes'
import { ExchangeDetails, ProductReturnCard, ProductReturnData } from './types/useReturnController.types'
import { getProductReturnCardFromOrder, getProductsFromOrder, trackReturnSuccess } from './useReturnController.helpers'

interface useReturnControllerProps {
  orderId: number
  isOrderSummaryPage: boolean
}

export const RETURN_STAGE_NO_VS_NAME = {
  SELECT_PRODUCTS: 0,
  SELECT_REFUND_AND_RETURN_TYPE: 1,
  SELECT_REFUND_AND_RETURN_SUMMERY: 2,
  RETURN_COMPLETE: 3,
}

const useReturnStage = (isInitialLoad: boolean, isOrderSummaryPage: boolean) => {
  const searchParams = useSearchParams()
  const router = useQuinceNprogressRouter()
  const returnStage = Number(searchParams.get('stage')) || 0
  const isFurnitureTesting = searchParams.get('isFurnitureTesting') === 'true'

  const setReturnStage = useCallback(
    (nextReturnStage: number) => {
      if (isOrderSummaryPage) return
      const updatedSearchParams = new URLSearchParams(
        isFurnitureTesting
          ? {
              // @i18n-ignore
              isFurnitureTesting: 'true',
            }
          : {},
      )
      updatedSearchParams.set('stage', String(nextReturnStage))
      router.push('?' + updatedSearchParams.toString())
    },
    [isOrderSummaryPage, isFurnitureTesting],
  )

  useEffect(() => {
    if (returnStage !== 0) {
      const updatedSearchParams = new URLSearchParams(
        isFurnitureTesting
          ? {
              // @i18n-ignore
              isFurnitureTesting: 'true',
            }
          : {},
      )
      updatedSearchParams.set('stage', '0')
      router.replace('?' + updatedSearchParams.toString())
    }
  }, [])

  return {
    returnStage,
    setReturnStage,
  }
}

const getStateChangeHandlers = (t: TFunction) => {
  const STATE_CHANGE_HANDLERS: Record<
    string,
    (data: {
      orderId: number
      productReturnCardsSelectedForReturn: ProductReturnCard[]
      setReturnSummary: React.Dispatch<React.SetStateAction<RefundSummary | undefined>>
      setReturnResponse: React.Dispatch<React.SetStateAction<ReturnResponse | undefined>>
    }) => Promise<void> | void
  > = {
    [RETURN_STAGE_NO_VS_NAME.SELECT_REFUND_AND_RETURN_TYPE]: ({ productReturnCardsSelectedForReturn }) => {
      if (!productReturnCardsSelectedForReturn.length) {
        throw new QuinceError(t('minimum_selection_required'), t('minimum_selection_required'))
      }
    },
    [RETURN_STAGE_NO_VS_NAME.SELECT_REFUND_AND_RETURN_SUMMERY]: async ({
      orderId,
      productReturnCardsSelectedForReturn,
      setReturnSummary,
    }) => {
      try {
        const returnSummary = await ordersAndReturnService.getReturnSummary(
          orderId,
          productReturnCardsSelectedForReturn,
        )
        setReturnSummary(returnSummary)
      } catch (error) {
        throw QuinceError.fromError(error as Error, undefined, t)
      }
    },
    [RETURN_STAGE_NO_VS_NAME.RETURN_COMPLETE]: async ({
      orderId,
      productReturnCardsSelectedForReturn,
      setReturnResponse,
    }) => {
      try {
        const returnResponse = await ordersAndReturnService.createReturn(orderId, productReturnCardsSelectedForReturn)
        trackReturnSuccess({
          productReturnCards: productReturnCardsSelectedForReturn,
        })
        setReturnResponse(returnResponse)
      } catch (error) {
        throw QuinceError.fromError(error as Error, undefined, t)
      }
    },
  }

  return STATE_CHANGE_HANDLERS
}

const useReturnController = ({ orderId, isOrderSummaryPage }: useReturnControllerProps) => {
  const { t } = useTranslation('common')
  const [productReturnCards, setProductReturnCards] = useImmer<ProductReturnCard[]>([])
  const [order, setOrder] = useState<Order>()
  const [fetchingOrders, setFetchingOrders] = useState<boolean>(false)
  const { returnStage, setReturnStage } = useReturnStage(!order, isOrderSummaryPage)
  const [selectedProductReturnCardId, setSelectedProductReturnCardId] = useState<string>()
  const [returnSummary, setReturnSummary] = useState<RefundSummary>()
  const [returnResponse, setReturnResponse] = useState<ReturnResponse>()
  const [products, setProducts] = useState<Product[] | undefined>([])

  const productReturnCardsSelectedForReturn = useMemo(
    () => productReturnCards.filter(productReturnCard => productReturnCard.selectedForReturn),
    [productReturnCards],
  )

  const STATE_CHANGE_HANDLERS = getStateChangeHandlers(t)

  const changeReturnStage = useCallback(
    async (
      nextStage: number,
      options?: {
        forceReturnStateChange: boolean
      },
    ) => {
      const stateChangeHandler = STATE_CHANGE_HANDLERS[nextStage]
      if (!stateChangeHandler) {
        setReturnStage(nextStage)
        return
      }
      try {
        await stateChangeHandler({
          orderId,
          productReturnCardsSelectedForReturn,
          setReturnSummary,
          setReturnResponse,
        })
        setReturnStage(nextStage)
      } catch (error) {
        captureException(error, {
          module: MODULES.RETURN_CONTROLLER,
          feature: FEATURES.RETURN_STEP_CHANGE,
          extraInfo: {
            // @i18n-ignore
            message: 'Error while changing return step',
            nextStage,
          },
        })

        if (options?.forceReturnStateChange) setReturnStage(nextStage)
        throw error
      }
    },
    [orderId, productReturnCardsSelectedForReturn, setReturnStage],
  )

  useEffect(() => {
    async function fetchProductOrders() {
      if (!orderId) {
        return
      }
      setFetchingOrders(true)
      try {
        const order = await ordersAndReturnService.getOrderByOrderId(orderId)
        const products = await getProductsFromOrder(order, !isOrderSummaryPage)
        setProducts(products)
        setOrder(order)
        if (order?.shipments) setProductReturnCards(getProductReturnCardFromOrder(order, !isOrderSummaryPage))
      } catch (error: unknown) {
        QuinceError.toastFromError(error as Error, {
          module: MODULES.RETURN_CONTROLLER,
          feature: FEATURES.GET_ORDER_DETAILS,
          t,
        })
      } finally {
        setFetchingOrders(false)
      }
    }
    void fetchProductOrders()
  }, [orderId, isOrderSummaryPage])

  const updateProductReturnCard = (productReturnCardId: string, productReturnData: ProductReturnData) => {
    setProductReturnCards((productReturnCards: ProductReturnCard[]) => {
      const targetProductReturnCard = productReturnCards.find(
        productReturnCard => productReturnCard.id === productReturnCardId,
      )
      if (!targetProductReturnCard) return
      targetProductReturnCard.selectedForReturn = true
      targetProductReturnCard.returnQuantity =
        productReturnData.returnQuantity || targetProductReturnCard.lineItem.quantity
      targetProductReturnCard.reasonCode = productReturnData.reasonCode
      targetProductReturnCard.subReason = productReturnData.subReason
      targetProductReturnCard.photos = productReturnData.photos
      targetProductReturnCard.rating = productReturnData.rating
      targetProductReturnCard.additionalComments = productReturnData.additionalComments
    })
  }

  const setReturnType = useCallback(
    (productReturnCardId: string, returnType: RETURN_TYPES) => {
      setProductReturnCards((productReturnCards: ProductReturnCard[]) => {
        const targetProductReturnCard = productReturnCards.find(
          productReturnCard => productReturnCard.id === productReturnCardId,
        )
        if (!targetProductReturnCard) return
        targetProductReturnCard.returnType = returnType
      })
    },
    [setProductReturnCards],
  )

  const setReturnTypeExchange = useCallback(
    (productReturnCardId: string, exchangeDetails: ExchangeDetails) => {
      setProductReturnCards((productReturnCards: ProductReturnCard[]) => {
        const targetProductReturnCard = productReturnCards.find(
          productReturnCard => productReturnCard.id === productReturnCardId,
        )
        if (!targetProductReturnCard) return
        targetProductReturnCard.returnType = RETURN_TYPES.EXCHANGE
        targetProductReturnCard.exchangeDetails = exchangeDetails
      })
    },
    [setProductReturnCards],
  )

  const toggleProductReturnCardSelection = (productReturnCardId: string) => {
    let isUnselected = false
    setProductReturnCards((productReturnCards: ProductReturnCard[]) => {
      const targetProductReturnCard = productReturnCards.find(
        productReturnCard => productReturnCard.id === productReturnCardId,
      )
      if (!targetProductReturnCard) return
      if (targetProductReturnCard.selectedForReturn) {
        targetProductReturnCard.selectedForReturn = false
        if (selectedProductReturnCardId === targetProductReturnCard.id) {
          setSelectedProductReturnCardId(undefined)
        }
        isUnselected = true
      } else {
        setSelectedProductReturnCardId(targetProductReturnCard.id)
      }
    })
    return isUnselected
  }

  const selectedProductReturnCard = productReturnCards.find(
    productReturnCard => productReturnCard.id === selectedProductReturnCardId,
  )

  const getProductByLineItem = useCallback(
    (lineItem: LineItem) => {
      if (lineItem.productId === FREE_TOTE_SPID) {
        return FREE_TOTE_PRODUCT[0]
      }
      return products?.find(product => product.spid === lineItem.productId) ?? lineItem
    },
    [products],
  )

  const selectedProduct = selectedProductReturnCard && getProductByLineItem(selectedProductReturnCard.lineItem)

  return {
    products,
    productReturnCards,
    selectedProductReturnCard,
    selectedProduct,
    setSelectedProductReturnCardId,
    updateProductReturnCard,
    toggleProductReturnCardSelection,
    productReturnCardsSelectedForReturn,
    setReturnType,
    setReturnTypeExchange,
    fetchingOrders,
    returnSummary,
    returnStage,
    changeReturnStage,
    returnResponse,
    order,
    getProductByLineItem,
  }
}

export default useReturnController
